import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from './useAuth';
import { toast } from 'sonner';

export interface RecommendationNode {
  node_id: string;
  title: string;
  difficulty_level: number;
  estimated_study_time: number;
  prerequisite_status: {
    all_completed: boolean;
    completion_rate: number;
    has_prerequisites: boolean;
    missing_prerequisites: string[];
    completed_prerequisites: string[];
  };
  recommendation_score: string;
  content_type?: string;
  node_type?: string;
  discipline_name?: string;
  competency_tags?: any;
  learning_objectives?: any;
}

export interface EnhancedRecommendation extends RecommendationNode {
  matchReason: string;
  confidenceScore: number;
  userProgress?: {
    completion_rate: number;
    time_spent: number;
    last_accessed: string | null;
  };
  relevanceFactors: {
    difficulty_match: boolean;
    learning_style_match: boolean;
    prerequisite_ready: boolean;
    interest_alignment: number;
  };
}

// Hook for getting smart recommendations
export const useRecommendations = (limit: number = 10) => {
  const { user } = useAuth();

  return useQuery({
    queryKey: ['recommendations', user?.id, limit],
    queryFn: async (): Promise<EnhancedRecommendation[]> => {
      if (!user?.id) {
        throw new Error('User not authenticated');
      }

      console.log('🎯 Fetching recommendations for user:', user.id);

      // Get user preferences for enhanced matching
      const { data: userPrefs } = await supabase
        .from('user_preferences')
        .select('learning_style, preferred_difficulty, weekly_study_goal, study_time_availability')
        .eq('user_id', user.id)
        .single();

      console.log('👤 User preferences:', userPrefs);

             // Get smart recommendations from the database function
       const { data: baseRecommendations, error } = await supabase
         .rpc('get_next_recommended_nodes', {
           p_user_id: user.id,
           p_discipline_id: null,
           p_difficulty_level: null,
           p_limit: limit
         });

      if (error) {
        console.error('❌ Error fetching recommendations:', error);
        throw error;
      }

      if (!baseRecommendations || baseRecommendations.length === 0) {
        console.log('📭 No recommendations found');
        return [];
      }

      console.log('📚 Base recommendations found:', baseRecommendations.length);

      // Enhance recommendations with additional metadata
      const nodeIds = baseRecommendations.map(r => r.node_id);
      
      const { data: nodeDetails } = await supabase
        .from('nodes')
        .select(`
          id,
          content_type,
          node_type,
          competency_tags,
          learning_objectives,
          discipline_id,
          disciplines!inner(name)
        `)
        .in('id', nodeIds);

      // Get user progress for these nodes
      const { data: userProgress } = await supabase
        .from('user_progress')
        .select('node_id, progress_percentage, time_spent_seconds, last_accessed_at')
        .eq('user_id', user.id)
        .in('node_id', nodeIds);

             // Enhance each recommendation with additional context
       const enhancedRecommendations: EnhancedRecommendation[] = baseRecommendations.map(rec => {
         const nodeDetail = nodeDetails?.find(n => n.id === rec.node_id);
         const progress = userProgress?.find(p => p.node_id === rec.node_id);
         
         // Parse prerequisite status from JSON
         const prerequisiteStatus = typeof rec.prerequisite_status === 'string' 
           ? JSON.parse(rec.prerequisite_status) 
           : rec.prerequisite_status as {
               all_completed: boolean;
               completion_rate: number;
               has_prerequisites: boolean;
               missing_prerequisites: string[];
               completed_prerequisites: string[];
             };
         
         // Calculate relevance factors
         const relevanceFactors = {
           difficulty_match: userPrefs?.preferred_difficulty ? 
             Math.abs(rec.difficulty_level - userPrefs.preferred_difficulty) <= 1 : true,
           learning_style_match: true, // Simplified for now
           prerequisite_ready: prerequisiteStatus.all_completed,
           interest_alignment: 0.8 // Simplified scoring
         };

        // Generate match reason
        let matchReason = 'Personalized for you';
        if (relevanceFactors.prerequisite_ready && relevanceFactors.difficulty_match) {
          matchReason = 'Perfect match - ready to learn';
        } else if (relevanceFactors.prerequisite_ready) {
          matchReason = 'Prerequisites completed';
        } else if (relevanceFactors.difficulty_match) {
          matchReason = 'Matches your preferred difficulty';
        }

        // Calculate confidence score
        const confidenceScore = Math.min(
          parseFloat(rec.recommendation_score) * 0.6 +
          (relevanceFactors.prerequisite_ready ? 20 : 0) +
          (relevanceFactors.difficulty_match ? 15 : 0) +
          (relevanceFactors.interest_alignment * 5),
          99
        );

                 return {
           ...rec,
           prerequisite_status: prerequisiteStatus,
           content_type: nodeDetail?.content_type,
           node_type: nodeDetail?.node_type,
           discipline_name: nodeDetail?.disciplines?.name,
           competency_tags: nodeDetail?.competency_tags,
           learning_objectives: nodeDetail?.learning_objectives,
           matchReason,
           confidenceScore: Math.round(confidenceScore),
           userProgress: progress ? {
             completion_rate: progress.progress_percentage || 0,
             time_spent: progress.time_spent_seconds || 0,
             last_accessed: progress.last_accessed_at
           } : undefined,
           relevanceFactors
         };
      });

      console.log('✨ Enhanced recommendations:', enhancedRecommendations.length);
      return enhancedRecommendations;
    },
    enabled: !!user?.id,
    staleTime: 5 * 60 * 1000, // 5 minutes
    refetchOnWindowFocus: false
  });
};

// Hook for refreshing recommendations
export const useRefreshRecommendations = () => {
  const queryClient = useQueryClient();
  const { user } = useAuth();

  return useMutation({
    mutationFn: async () => {
      if (!user?.id) throw new Error('User not authenticated');
      
      // Invalidate and refetch recommendations
      await queryClient.invalidateQueries({ 
        queryKey: ['recommendations', user.id] 
      });
      
      return true;
    },
    onSuccess: () => {
      toast.success('Recommendations refreshed!');
    },
    onError: (error) => {
      console.error('Error refreshing recommendations:', error);
      toast.error('Failed to refresh recommendations');
    }
  });
};

// Hook for tracking recommendation interactions
export const useTrackRecommendation = () => {
  return useMutation({
    mutationFn: async ({ 
      nodeId, 
      action, 
      userId 
    }: { 
      nodeId: string; 
      action: 'viewed' | 'clicked' | 'dismissed'; 
      userId: string;
    }) => {
      // Log interaction for future recommendation improvements
      console.log(`📊 Tracking recommendation ${action}:`, nodeId);
      
      // Could store in analytics table for ML improvements
      return { nodeId, action, timestamp: new Date().toISOString() };
    }
  });
}; 